<template>
  <div class="acc">
    <nav class="acc__tabs">
      <button
        v-for="(t, i) in tabs"
        :key="t.id"
        type="button"
        class="acc__tab"
        :class="{ 'acc__tab--active': ui.tab === i }"
        @click="ui.tab = i"
      >
        <v-icon small>{{ t.icon }}</v-icon>
        <span>{{ $t(t.title) }}</span>
      </button>
    </nav>

    <!-- General: code length + other options -->
    <div v-if="ui.tab === 0" class="acc__pane">
      <v-progress-linear v-if="!render" indeterminate color="primary" />
      <v-form v-if="render" ref="form" v-model="valid" class="acc__form" lazy-validation>
        <div class="acc__body">
          <section class="acc__block">
            <div class="acc__block-head">
              <div class="acc__block-title">{{ $t('codeLength') }}</div>
              <div class="acc__block-hint">{{ $t('accountingCodeLengthHint') }}</div>
            </div>
            <div class="acc__code-grid">
              <v-text-field
                v-model="accountingGroupLength"
                :label="$t('group')"
                :rules="[rules.required, rules.counter]"
                inputmode="numeric"
                maxlength="1"
                outlined
                dense
                hide-details="auto"
              />
              <v-text-field
                v-model="accountingTotalLength"
                :label="$t('total')"
                :rules="[rules.required, rules.counter]"
                inputmode="numeric"
                maxlength="1"
                outlined
                dense
                hide-details="auto"
              />
              <v-text-field
                v-model="accountingGivenLength"
                :label="$t('given')"
                :rules="[rules.required, rules.counter]"
                inputmode="numeric"
                maxlength="1"
                outlined
                dense
                hide-details="auto"
              />
              <div class="acc__sum">
                <span class="acc__sum-label">{{ $t('sum') }}</span>
                <span class="acc__sum-value">{{ codeSum }}</span>
              </div>
            </div>
            <div class="acc__preview">
              <span class="acc__preview-label">{{ $t('accountingCodePreview') }}</span>
              <div class="acc__preview-code">
                <template v-for="(seg, i) in codeSegments">
                  <span v-if="i" :key="'sep' + i" class="acc__preview-sep">-</span>
                  <span :key="'seg' + i" class="acc__preview-seg" :class="'acc__preview-seg--' + i">
                    <span v-for="n in seg" :key="n" class="acc__preview-digit">{{ n }}</span>
                  </span>
                </template>
              </div>
            </div>
          </section>

          <section class="acc__block">
            <div class="acc__block-head">
              <div class="acc__block-title">{{ $t('other') }}</div>
            </div>
            <div
              class="acc__toggle"
              role="switch"
              tabindex="0"
              :aria-checked="accountingFillWithContent === 'active' ? 'true' : 'false'"
              @click="toggleFill"
              @keydown.space.prevent="toggleFill"
            >
              <span>{{ $t('fillArticleWithContentIfEmpty') }}</span>
              <v-switch
                :input-value="accountingFillWithContent === 'active'"
                class="acc__switch"
                color="primary"
                inset
                hide-details
                readonly
                dense
              />
            </div>
          </section>
        </div>
        <div class="acc__foot">
          <v-btn color="primary" depressed class="app-form-action-btn" :loading="isLoading" @click="create">
            <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('submit') }}
          </v-btn>
        </div>
      </v-form>
    </div>

    <!-- Detail-account mappings -->
    <template v-for="(t, i) in tabs">
      <accounting-given-list
        v-if="i > 0"
        v-show="ui.tab === i"
        :key="t.id"
        class="acc__pane"
        :select="t.select"
        :type-prop="t.type"
        :hint="$t(t.hint)"
      />
    </template>

    <v-snackbar
      v-model="snakbar"
      :color="(snakbarColor ? snakbarColor : 'primary')"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snakbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
  import Vue from 'vue'
  import AccountingGivenList from './AccountingGivenList.vue'
  import General from '../../../../../Mixins/general'
  export default {
    name: 'Customer',
    components: {
      AccountingGivenList,
    },
    mixins: [General],
    data: () => ({
      accountingGroupLength: 2,
      accountingTotalLength: 2,
      accountingGivenLength: 2,
      rules: {
        required: value => !!value || 'Required.',
        counter: value => String(value || '').length <= 1 || 'Max 1 characters',
      },
      valid: true,
      select: 'accounting',
      accountingFillWithContent: null,
      render: false,
      isLoading: false,
    }),
    computed: {
      codeSum () {
        return +this.accountingGroupLength + +this.accountingTotalLength + +this.accountingGivenLength
      },
      codeSegments () {
        return [this.accountingGroupLength, this.accountingTotalLength, this.accountingGivenLength]
          .map(v => {
            const n = parseInt(v, 10)
            return n > 0 && n < 10 ? n : 0
          })
      },
    },
    watch: {
    },
    created () {
      // UI-only state lives outside data() so create() (which posts $data)
      // never sends it to the API.
      this.ui = Vue.observable({ tab: 0 })
      this.tabs = [
        { id: 'general', icon: 'mdi-tune-variant', title: 'general' },
        { id: 'given', icon: 'mdi-book-cog-outline', title: 'givenSetting', select: 'getGivenSetting', hint: 'accountingGivenSettingHint' },
        { id: 'invoice', icon: 'mdi-swap-horizontal', title: 'sell&buy', select: 'getGivenTypes', type: 'invoice', hint: 'accountingInvoiceGivenHint' },
        { id: 'entrepot', icon: 'mdi-warehouse', title: 'entrepot', select: 'getGivenTypes', type: 'entrepot', hint: 'accountingEntrepotGivenHint' },
        { id: 'kind', icon: 'mdi-shape-outline', title: 'kindProductGiven', select: 'getKindProductGiven', hint: 'accountingKindGivenHint' },
      ]
    },
    activated () {
      this.show()
    },
    beforeMount () {
      this.rules = {
        required: value => !!value || this.$t('is-required'),
        counter: value => String(value || '').length <= 1 || 'Max 1 characters',
      }
    },
    mounted () {
      this.show()
    },
    methods: {
      toggleFill () {
        this.accountingFillWithContent = this.accountingFillWithContent === 'active' ? null : 'active'
      },
      create () {
        if (!this.$refs.form.validate()) return false
        var app = this
        app.isLoading = true
        app.$axios.post(this.$endpoint + 'setting', this.$data)
          .then(res => {
            app.snakbarText = app.$t('registered')
            app.snakbarColor = 'green'
            app.snakbar = true
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => {
            this.isLoading = false
          })
      },
      show () {
        var app = this
        app.loading = true
        app.$axios.get(this.$endpoint + 'setting/' + 1 + '/edit', {
          params: {
            select: app.select,
          },
        })
          .then(res => {
            app.$nextTick(() => {
              res = res.data.data
              app.accountingGroupLength = res.accountingGroupLength
              app.accountingTotalLength = res.accountingTotalLength
              app.accountingGivenLength = res.accountingGivenLength
              app.accountingFillWithContent = res.accountingFillWithContent
              app.render = true
            })
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
    },
  }
</script>

<style scoped>
.acc {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.acc__tabs {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 10px 14px;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.acc__tabs::-webkit-scrollbar { display: none; }
.acc__tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 0.84rem;
  color: inherit;
  opacity: 0.75;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}
.acc__tab:hover { background: rgba(15, 23, 42, 0.04); opacity: 1; }
.acc__tab--active,
.acc__tab--active:hover {
  background: rgba(25, 118, 210, 0.1);
  color: var(--v-primary-base, #1976d2);
  font-weight: 600;
  opacity: 1;
}
.acc__tab--active .v-icon { color: inherit !important; }
.acc__pane {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.acc__form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.acc__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.acc__block {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 16px;
}
.theme--dark .acc__block { border-color: rgba(255, 255, 255, 0.12); }
.acc__block-head { margin-bottom: 14px; }
.acc__block-title { font-size: 0.9rem; font-weight: 700; }
.acc__block-hint { font-size: 0.78rem; opacity: 0.6; margin-top: 2px; line-height: 1.6; }
.acc__code-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 140px)) auto;
  gap: 12px;
  align-items: start;
}
.acc__code-grid ::v-deep .v-input { margin: 0 !important; padding: 0 !important; height: auto !important; }
.acc__sum {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  background: rgba(25, 118, 210, 0.08);
  justify-self: start;
}
.acc__sum-label { font-size: 0.8rem; opacity: 0.7; }
.acc__sum-value { font-size: 1rem; font-weight: 700; color: var(--v-primary-base, #1976d2); }
.acc__preview {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.acc__preview-label { font-size: 0.78rem; opacity: 0.6; }
.acc__preview-code {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  direction: ltr;
}
.acc__preview-seg { display: inline-flex; gap: 3px; }
.acc__preview-digit {
  width: 22px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #fff;
}
.acc__preview-seg--0 .acc__preview-digit { background: #1e88e5; }
.acc__preview-seg--1 .acc__preview-digit { background: #43a047; }
.acc__preview-seg--2 .acc__preview-digit { background: #fb8c00; }
.acc__preview-sep { opacity: 0.4; font-weight: 700; }
.acc__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 4px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.875rem;
}
.acc__toggle:hover { background: rgba(15, 23, 42, 0.03); }
.acc__switch { margin: 0 !important; padding: 0 !important; pointer-events: none; }
.acc__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 12px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
  background: #fafbfd;
}
.theme--dark .acc__foot { background: rgba(255, 255, 255, 0.03); }

@media (max-width: 799px) {
  .acc__tabs { padding: 10px 12px; }
  .acc__body { padding: 12px; }
  .acc__code-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .acc__code-grid { row-gap: 16px; }
  .acc__sum { grid-column: 1 / -1; justify-self: stretch; justify-content: space-between; margin-top: 4px; }
  .acc__foot { padding: 10px 12px calc(10px + env(safe-area-inset-bottom)); }
  .acc__foot .v-btn { flex: 1 1 auto; }
}
</style>
